import {random, sample, range, isNumber, sampleSize} from 'lodash';
import * as SampleData from '@packages/shared/constants/SampleData';
import {normalizeRichText} from '@packages/systems/core/utils/TextUtils';
import {slugify} from '@packages/utilities/string-utils';
import {paths as sharedPaths} from '@packages/systems/core/constants/SharedConfig';
import {generateGuid} from '@packages/utilities/guid';
import type {Property} from '@packages/systems/commerce/core';

export type SampleDataCategory =
  | 'general'
  | 'people'
  | 'post'
  | 'aiPlaceholder';

export const itemCategories = {
  GENERAL: 'general',
  PEOPLE: 'people',
  POST: 'post',
  AI_PLACEHOLDER: 'aiPlaceholder',
} as const;

export const numberOfSampleImageVariants = {
  PEOPLE: 20,
  GENERAL: 20,
} as const;

const loremIpsum = [
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
  'Maecenas euismod sapien eu arcu convallis, vitae vestibulum ipsum maximus.',
  'Fusce aliquet turpis at orci bibendum, non convallis justo tempor.',
  'Vivamus vitae arcu vel velit efficitur vestibulum vel in purus.',
  'Sed auctor augue id tellus lacinia, nec ultricies est fermentum.',
  'Integer rutrum ante et nunc venenatis, id ultricies risus ultricies.',
  'Cras sit amet velit id nulla tempus dictum sit amet eu nisi.',
  'Donec eu felis at libero consequat sagittis a et urna.',
  'Praesent nec orci at nulla consequat congue ut non arcu.',
  'Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae.',
];

function generateRandomLoremIpsum() {
  return sample(loremIpsum) as string;
}

function generateLoremWords(count: number) {
  const loremWords = [
    'lorem',
    'ipsum',
    'dolor',
    'sit',
    'amet',
    'consectetur',
    'adipiscing',
    'elit',
    'sed',
    'do',
    'eiusmod',
    'tempor',
    'incididunt',
    'ut',
    'labore',
    'et',
    'dolore',
    'magna',
    'aliqua',
  ];

  const words = [];
  for (let i = 0; i < count; i++) {
    words.push(sample(loremWords) as string);
  }

  return words.join(' ');
}

function generateFirstName() {
  const firstNames = [
    'John',
    'Jane',
    'Michael',
    'Emily',
    'David',
    'Sarah',
    'Daniel',
    'Jennifer',
    'Matthew',
    'Jessica',
  ];

  const randomIndex = Math.floor(Math.random() * firstNames.length);
  return firstNames[randomIndex];
}

function generateLastName() {
  const lastNames = [
    'Smith',
    'Johnson',
    'Williams',
    'Jones',
    'Brown',
    'Davis',
    'Miller',
    'Wilson',
    'Moore',
    'Taylor',
  ];

  const randomIndex = Math.floor(Math.random() * lastNames.length);
  return lastNames[randomIndex];
}

export function generateNames(
  amount: number,
  category: SampleDataCategory
): string[] {
  switch (category) {
    case itemCategories.PEOPLE:
      return range(0, amount).map(() => {
        return generateFirstName() + ' ' + generateLastName();
      });
    case itemCategories.POST:
      return sampleSize(SampleData.postTitles, amount);
    default:
      // Falling back to itemCategories.GENERAL
      return range(0, amount).map(() => {
        const numberOfWords = random(1, 4);
        const fakeName = generateLoremWords(numberOfWords);
        return fakeName.replace(/\b\w/g, (l) => l.toUpperCase());
      });
  }
}

export function generateLink() {
  return sample(SampleData.links);
}

export function generatePlainText(
  minLength?: number | null,
  maxLength?: number | null,
  singleLine?: boolean | null
) {
  const separator = singleLine ? ' ' : '\n';
  const defaultMaxValue = singleLine ? 140 : 300;
  const maxValue = maxLength ? maxLength : defaultMaxValue;
  const minValue = isNumber(minLength) ? minLength : random(0, maxValue);

  const numberOfCharacters = random(minValue, maxValue);

  let plainText = '';

  while (plainText.length < numberOfCharacters) {
    plainText = generateRandomLoremIpsum() + separator + plainText;
  }

  return plainText.substring(0, numberOfCharacters);
}

export function generateImageUrl(category: SampleDataCategory) {
  if (category === itemCategories.AI_PLACEHOLDER) {
    return sharedPaths.AI_PLACEHOLDER_IMG_URL;
  }
  return category === itemCategories.PEOPLE
    ? sharedPaths.PEOPLE_SAMPLE_IMG_PATH +
        random(1, numberOfSampleImageVariants.PEOPLE) +
        '.jpg'
    : sharedPaths.GENERAL_SAMPLE_IMG_PATH +
        random(1, numberOfSampleImageVariants.GENERAL) +
        '.jpg';
}

export function generateVideo() {
  return sample(SampleData.videos);
}

export function generateEmail() {
  const emailProviders = [
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'icloud.com',
  ];

  const domains = ['example', 'test', 'company', 'user', 'random', 'demo'];

  return sample(domains) + '@' + sample(emailProviders);
}

export function generatePhone() {
  const number =
    '+1 ' + Number(Math.floor(Math.random() * 90000000 + 100000000));
  const fakePhone = number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return fakePhone;
}

export function generateNumber(
  minValue?: number | null,
  maxValue?: number | null,
  precision?: number | null,
  allowNegative?: boolean | null,
  format?: string | null
) {
  const min = minValue ? Number(minValue) : 0;
  const max = maxValue ? maxValue : 99999;
  const factor = generateBoolean() ? -1 : 1;
  const p = format === 'integer' ? 0 : precision;

  // Generate a random number within the specified range
  let fakeNumber = random(min, max);

  // Round the number to the specified precision
  fakeNumber = parseFloat(fakeNumber.toFixed(p || 0));

  // Check if negative numbers are allowed and adjust the number accordingly
  if (allowNegative && factor * fakeNumber > min) {
    return factor * fakeNumber;
  } else {
    return fakeNumber;
  }
}

export function generateDate(format?: string | null) {
  const currentDate = new Date();

  const date = generateBoolean()
    ? new Date(
        currentDate.getTime() + Math.random() * (365 * 24 * 60 * 60 * 1000)
      ) // future date
    : new Date(
        currentDate.getTime() - Math.random() * (365 * 24 * 60 * 60 * 1000)
      ); // past date;

  if (format === 'date-time') {
    return date.toISOString();
  } else {
    return date.toISOString().slice(0, 10) + 'T00:00:00.000Z';
  }
}

export function generateBoolean() {
  return Math.random() < 0.5;
}

export function generateColor() {
  return sample(SampleData.colors);
}

function getRichTextBlock(tag: string, maxValue: number) {
  let richTextBlock = '';
  const hTags = ['h1', 'h2', 'h3'];
  if (hTags.indexOf(tag) !== -1) {
    richTextBlock = generateRandomLoremIpsum();
  } else if (tag === 'p' || tag === 'blockquote') {
    richTextBlock = Array.from({length: random(1, 3)}, () =>
      generateRandomLoremIpsum()
    ).join(' ');
  }
  const result = richTextBlock.slice(0, maxValue).trim();
  return '<' + tag + '>' + result + '</' + tag + '>';
}

export function generateRichText(min?: number | null, max?: number | null) {
  const maxLength = max ? max : 2500;
  const minLength = min ? min : random(0, maxLength);
  const textBlocks = ['h2', 'p', 'h3', 'blockquote', 'p', 'p'];

  let richText = '';
  let normalizedRichText = '';

  while (normalizedRichText.length < minLength) {
    for (let i = 0; i < textBlocks.length; i++) {
      if (normalizedRichText.length >= maxLength) {
        break;
      }
      richText += getRichTextBlock(
        // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        textBlocks[i],
        maxLength - normalizedRichText.length
      );
      normalizedRichText = normalizeRichText(richText);
    }
  }

  return richText;
}

const personSynonymsList = [
  'admin',
  'artist',
  'approver',
  'author',
  'blogger',
  'client',
  'creator',
  'editor',
  'owner',
  'people',
  'person',
  'team member',
  'writer',
];

const postSynonymsList = [
  'article',
  'composition',
  'essay',
  'item',
  'piece',
  'post',
  'publication',
  'report',
  'story',
];

export function getItemCategory(collectionName: string) {
  const collName = collectionName.toLowerCase();
  const isSynonymousWithPerson = personSynonymsList.some(
    (synonym) => collName.indexOf(synonym) !== -1
  );
  const isSynonymousWithPost = postSynonymsList.some(
    (synonym) => collName.indexOf(synonym) !== -1
  );

  if (isSynonymousWithPerson) {
    return itemCategories.PEOPLE;
  } else if (isSynonymousWithPost) {
    return itemCategories.POST;
  }
  return itemCategories.GENERAL;
}

export function generatePrice(
  max: number,
  min: number,
  defaultCurrency?: string
) {
  return {
    value: random(min, max),
    unit: defaultCurrency || 'USD',
  };
}

function generateProductMaterial() {
  return sample([
    'Cotton',
    'Wool',
    'Leather',
    'Silk',
    'Polyester',
    'Nylon',
    'Denim',
    'Satin',
    'Velvet',
    'Linen',
  ]) as string;
}

const generateOptionNames = (numOfOptions: number): Array<string> => {
  const names: Array<string> = [];
  const randomNumber = random(1, 2);
  const generateName = (existingNames: Array<string>): string => {
    const name =
      randomNumber % 2
        ? (sample(Object.keys(SampleData.colors)) as string)
        : generateProductMaterial();
    return existingNames.includes(name) ? generateName(existingNames) : name;
  };

  for (let i = 0; i < numOfOptions; i++) {
    names.push(generateName(names));
  }
  return names;
};

const makeOptionSet = (name: string, options: string[]): Property => ({
  id: generateGuid(32),
  name,
  enum: options.map((optionName) => ({
    id: generateGuid(32),
    slug: slugify(optionName),
    name: optionName,
  })),
});

const getNumOfVariantsBasedOnOptions = (optionSets: Array<number>): number =>
  optionSets.reduce(
    (numOfVariants, numOfOptions) => (numOfVariants *= numOfOptions),
    1
  );

const getOptionSets = (maxVariantsPerProduct: number): Array<number> => {
  const optionSets: Array<number> = [];
  let totalSets = 0;

  while (totalSets < 3) {
    // limit max number of option sets to 3 per product
    const getNumOfOptions = (numOfOptions: number): number => {
      const numOfVariants = getNumOfVariantsBasedOnOptions([
        ...optionSets,
        numOfOptions,
      ]);

      return numOfVariants <= maxVariantsPerProduct || numOfOptions === 1
        ? numOfOptions
        : getNumOfOptions(numOfOptions - 1);
    };
    const numOfOptions = getNumOfOptions(3);
    totalSets++;

    if (totalSets === 3 && numOfOptions === 1) {
      // If there are already two sets, then we don't need a third one if it only has one option
      break;
    }

    optionSets.push(numOfOptions);
  }
  return optionSets;
};

const generateProductAdjective = () => {
  return sample([
    'Premium',
    'Luxurious',
    'High-quality',
    'Innovative',
    'Elegant',
    'Sleek',
    'Modern',
    'Stylish',
    'Functional',
    'Versatile',
  ]) as string;
};

export const generateSampleSkuProperties = (
  numOfProductsWithVariants: number,
  maxVariants: number
): Array<Array<Property>> => {
  const sampleSkuProperties: Array<Array<Property>> = [];
  const maxVariantsPerProduct = Math.floor(
    (maxVariants + numOfProductsWithVariants) / numOfProductsWithVariants
  );
  const optionSets = getOptionSets(maxVariantsPerProduct);
  for (let i = 0; i < numOfProductsWithVariants; i++) {
    const skuProperties: Array<Property> = [];

    optionSets.forEach((numOfOptions) => {
      const name = generateProductAdjective();
      const options = generateOptionNames(numOfOptions);
      const optionSet = makeOptionSet(name, options);
      skuProperties.push(optionSet);
    });

    sampleSkuProperties.push(skuProperties);
  }
  return sampleSkuProperties;
};
